import { useState, useEffect, useCallback } from 'react'
import { useLocation } from 'react-router-dom'
import { HelpCircle, Volume2, VolumeX, X, ChevronRight, ChevronLeft, Sparkles, Play, FileText } from 'lucide-react'
import voiceAssistant from '../utils/voiceAssistant'

// Sample text with clichés for tutorial
const SAMPLE_TEXT = `The meeting was a breath of fresh air for the team. At the end of the day, we need to think outside the box and push the envelope. It goes without saying that time flies when you're having fun, and we should leave no stone unturned in our pursuit of excellence.

Our cutting-edge solutions are second to none. The bottom line is that we need to hit the ground running and give 110% effort. In this day and age, actions speak louder than words.

Last but not least, Rome wasn't built in a day, so we should take it one step at a time while keeping our eyes on the prize.`

// Tutorial steps with element selectors for highlighting
const EDITOR_TUTORIAL_STEPS = [
    {
        id: 'welcome',
        title: 'Welcome to InkSpot! 👋',
        text: "Let me show you how to use InkSpot to detect clichés and improve your writing. Click Next to begin the tour.",
        element: null,
        position: 'center',
    },
    {
        id: 'editor',
        title: 'The Writing Editor',
        text: "This is where you write or paste your text. You can type directly here or paste content from anywhere.",
        element: '[data-tutorial="editor"]',
        position: 'bottom',
    },
    {
        id: 'sample',
        title: 'Try It Out! 📝',
        text: "Click the 'Load Sample' button below to insert example text full of clichés. This will help you see how InkSpot works!",
        element: '[data-tutorial="editor"]',
        position: 'bottom',
        showSampleButton: true,
    },
    {
        id: 'analyze',
        title: 'Analyze Your Text',
        text: "Click the Analyze button to scan your text. InkSpot's AI will identify clichés and overused phrases.",
        element: '[data-tutorial="analyze-btn"]',
        position: 'bottom',
    },
    {
        id: 'results',
        title: 'View Results',
        text: "Clichés will be highlighted in your text. Click on any highlighted phrase to see why it's overused and get fresh alternatives.",
        element: '[data-tutorial="results"]',
        position: 'left',
    },
    {
        id: 'stats',
        title: 'Writing Statistics',
        text: "Check your writing stats here - readability score, word count, and issues found. Aim for a high score!",
        element: '[data-tutorial="stats"]',
        position: 'left',
    },
    {
        id: 'complete',
        title: 'You\'re Ready! 🎉',
        text: "That's it! Start writing or paste your content, and I'll help you eliminate clichés. Need help anytime? Just click the help button!",
        element: null,
        position: 'center',
    },
]

const DASHBOARD_TUTORIAL_STEPS = [
    {
        id: 'welcome',
        title: 'Your Analytics Dashboard 📊',
        text: "This is your personal dashboard where you can track your writing progress and see detailed statistics.",
        element: null,
        position: 'center',
    },
    {
        id: 'stats',
        title: 'Overview Stats',
        text: "These cards show your key metrics: words analyzed, clichés detected, writing score, and AI requests used.",
        element: '[data-tutorial="dashboard-stats"]',
        position: 'bottom',
    },
    {
        id: 'chart',
        title: 'Usage Chart',
        text: "This chart shows your writing activity over time. Track how many words you've analyzed and clichés you've caught each day.",
        element: '[data-tutorial="usage-chart"]',
        position: 'bottom',
    },
    {
        id: 'score',
        title: 'Improvement Score',
        text: "Your improvement score shows how your writing quality is changing over time. Higher is better!",
        element: '[data-tutorial="improvement-score"]',
        position: 'left',
    },
    {
        id: 'complete',
        title: 'Keep Improving! 💪',
        text: "Visit the Editor to analyze more text and watch your stats improve. Happy writing!",
        element: null,
        position: 'center',
    },
]

export default function VoiceGuide() {
    const location = useLocation()
    const [isOpen, setIsOpen] = useState(false)
    const [isMuted, setIsMuted] = useState(false)
    const [currentStep, setCurrentStep] = useState(0)
    const [isActive, setIsActive] = useState(false) // Tutorial mode active
    const [highlightRect, setHighlightRect] = useState(null)
    const [isAvailable, setIsAvailable] = useState(true)

    // Get tutorial steps based on current page
    const getTutorialSteps = useCallback(() => {
        if (location.pathname === '/app') {
            return EDITOR_TUTORIAL_STEPS
        } else if (location.pathname === '/dashboard') {
            return DASHBOARD_TUTORIAL_STEPS
        }
        return EDITOR_TUTORIAL_STEPS
    }, [location.pathname])

    const tutorialSteps = getTutorialSteps()

    useEffect(() => {
        setIsAvailable(voiceAssistant.isAvailable())
        if (voiceAssistant.isAvailable()) {
            voiceAssistant.getVoices() // Preload voices
        }
    }, [])

    // Update highlight when step changes
    useEffect(() => {
        if (!isActive) {
            setHighlightRect(null)
            return
        }

        const step = tutorialSteps[currentStep]
        if (step?.element) {
            const el = document.querySelector(step.element)
            if (el) {
                // First scroll to element
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })

                // Then calculate rect after scroll completes (with delay)
                const updateRect = () => {
                    const rect = el.getBoundingClientRect()
                    setHighlightRect({
                        top: rect.top - 8,
                        left: rect.left - 8,
                        width: rect.width + 16,
                        height: rect.height + 16,
                    })
                }

                // Initial calculation after scroll animation (500ms)
                const scrollTimeout = setTimeout(updateRect, 500)

                // Also recalculate on scroll/resize
                const handleScrollResize = () => {
                    requestAnimationFrame(updateRect)
                }
                window.addEventListener('scroll', handleScrollResize, true)
                window.addEventListener('resize', handleScrollResize)

                return () => {
                    clearTimeout(scrollTimeout)
                    window.removeEventListener('scroll', handleScrollResize, true)
                    window.removeEventListener('resize', handleScrollResize)
                }
            } else {
                setHighlightRect(null)
            }
        } else {
            setHighlightRect(null)
        }
    }, [currentStep, isActive, tutorialSteps])

    // Speak current step
    useEffect(() => {
        if (isActive && !isMuted) {
            const step = tutorialSteps[currentStep]
            voiceAssistant.speak(step.text)
        }
    }, [currentStep, isActive, isMuted, tutorialSteps])

    const startTutorial = () => {
        setIsOpen(false)
        setCurrentStep(0)
        setIsActive(true)
    }

    const nextStep = () => {
        voiceAssistant.stop()
        if (currentStep < tutorialSteps.length - 1) {
            setCurrentStep(prev => prev + 1)
        } else {
            endTutorial()
        }
    }

    const prevStep = () => {
        voiceAssistant.stop()
        if (currentStep > 0) {
            setCurrentStep(prev => prev - 1)
        }
    }

    const endTutorial = () => {
        voiceAssistant.stop()
        setIsActive(false)
        setCurrentStep(0)
        setHighlightRect(null)
    }

    const loadSampleText = () => {
        // Find editor textarea and set value
        const editor = document.querySelector('[data-tutorial="editor"] textarea, [data-tutorial="editor"] [contenteditable]')
        if (editor) {
            if (editor.tagName === 'TEXTAREA') {
                editor.value = SAMPLE_TEXT
                // Trigger input event for React
                const event = new Event('input', { bubbles: true })
                editor.dispatchEvent(event)
            } else {
                editor.innerText = SAMPLE_TEXT
                const event = new Event('input', { bubbles: true })
                editor.dispatchEvent(event)
            }
        }
        // Also try via window
        if (window.setEditorContent) {
            window.setEditorContent(SAMPLE_TEXT)
        }
        // Move to next step
        nextStep()
    }

    const toggleMute = () => {
        if (!isMuted) {
            voiceAssistant.stop()
        }
        setIsMuted(!isMuted)
    }

    if (!isAvailable) return null

    const currentTutorialStep = tutorialSteps[currentStep]

    return (
        <>
            {/* Help Button - always visible when tutorial not active */}
            {!isActive && (
                <button
                    onClick={() => setIsOpen(true)}
                    className="fixed bottom-6 right-6 z-40 flex items-center gap-2 p-3 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 text-cyan-400 border border-cyan-500/30 rounded-full shadow-lg hover:shadow-xl hover:scale-105 hover:bg-cyan-500/30 transition-all duration-300 group"
                    title="Start Tutorial"
                >
                    <HelpCircle className="w-5 h-5" />
                </button>
            )}

            {/* Quick Menu */}
            {isOpen && !isActive && (
                <div className="fixed bottom-20 right-6 z-50 w-72 bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl shadow-2xl overflow-hidden animate-scale-in">
                    <div className="p-4 border-b border-white/10">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Sparkles className="w-5 h-5 text-cyan-400" />
                                <span className="text-white font-semibold">InkSpot Guide</span>
                            </div>
                            <button
                                onClick={() => setIsOpen(false)}
                                className="text-white/50 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    </div>
                    <div className="p-4 space-y-3">
                        <button
                            onClick={startTutorial}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-gradient-to-r from-cyan-500/20 to-purple-600/20 hover:from-cyan-500/30 hover:to-purple-600/30 border border-cyan-500/30 rounded-xl transition-all"
                        >
                            <Play className="w-5 h-5 text-cyan-400" />
                            <div className="text-left">
                                <div className="text-white font-medium">Start Tutorial</div>
                                <div className="text-white/50 text-xs">Interactive walkthrough</div>
                            </div>
                        </button>
                        <button
                            onClick={() => {
                                loadSampleText()
                                setIsOpen(false)
                            }}
                            className="w-full flex items-center gap-3 px-4 py-3 bg-white/5 hover:bg-white/10 border border-white/10 rounded-xl transition-all"
                        >
                            <FileText className="w-5 h-5 text-white/70" />
                            <div className="text-left">
                                <div className="text-white font-medium">Load Sample Text</div>
                                <div className="text-white/50 text-xs">Try with example clichés</div>
                            </div>
                        </button>
                    </div>
                </div>
            )}

            {/* Tutorial Overlay */}
            {isActive && (
                <>
                    {/* Backdrop with cutout */}
                    <div className="fixed inset-0 z-[100] pointer-events-none">
                        <svg className="w-full h-full">
                            <defs>
                                <mask id="tutorial-mask">
                                    <rect x="0" y="0" width="100%" height="100%" fill="white" />
                                    {highlightRect && (
                                        <rect
                                            x={highlightRect.left}
                                            y={highlightRect.top}
                                            width={highlightRect.width}
                                            height={highlightRect.height}
                                            rx="12"
                                            fill="black"
                                        />
                                    )}
                                </mask>
                            </defs>
                            <rect
                                x="0"
                                y="0"
                                width="100%"
                                height="100%"
                                fill="rgba(0,0,0,0.75)"
                                mask="url(#tutorial-mask)"
                            />
                        </svg>
                    </div>

                    {/* Highlight border animation */}
                    {highlightRect && (
                        <div
                            className="fixed z-[101] border-2 border-cyan-400 rounded-xl pointer-events-none animate-pulse"
                            style={{
                                top: highlightRect.top,
                                left: highlightRect.left,
                                width: highlightRect.width,
                                height: highlightRect.height,
                                boxShadow: '0 0 20px rgba(34, 211, 238, 0.5), 0 0 40px rgba(34, 211, 238, 0.3)'
                            }}
                        />
                    )}

                    {/* Fixed Bottom Subtitle Bar */}
                    <div className="fixed bottom-0 left-0 right-0 z-[102] pointer-events-auto">
                        {/* Progress bar */}
                        <div className="h-1 bg-slate-800">
                            <div
                                className="h-full bg-gradient-to-r from-cyan-500 to-purple-600 transition-all duration-500"
                                style={{ width: `${((currentStep + 1) / tutorialSteps.length) * 100}%` }}
                            />
                        </div>

                        {/* Subtitle content */}
                        <div className="bg-slate-900/98 backdrop-blur-xl border-t border-cyan-500/30">
                            <div className="max-w-4xl mx-auto px-6 py-4">
                                <div className="flex items-center justify-between gap-6">
                                    {/* Left: Title & Text */}
                                    <div className="flex-1 min-w-0">
                                        <div className="flex items-center gap-3 mb-1">
                                            <h3 className="text-lg font-bold text-white">
                                                {currentTutorialStep.title}
                                            </h3>
                                            <span className="text-xs text-white/50 bg-white/10 px-2 py-0.5 rounded-full">
                                                {currentStep + 1}/{tutorialSteps.length}
                                            </span>
                                        </div>
                                        <p className="text-white/70 text-sm leading-relaxed">
                                            {currentTutorialStep.text}
                                        </p>
                                    </div>

                                    {/* Right: Controls */}
                                    <div className="flex items-center gap-3 flex-shrink-0">
                                        {/* Sample button if applicable */}
                                        {currentTutorialStep.showSampleButton && (
                                            <button
                                                onClick={loadSampleText}
                                                className="flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                                            >
                                                <FileText className="w-4 h-4" />
                                                Load Sample
                                            </button>
                                        )}

                                        {/* Mute button */}
                                        <button
                                            onClick={toggleMute}
                                            className="p-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                            title={isMuted ? "Unmute" : "Mute"}
                                        >
                                            {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                                        </button>

                                        {/* Skip button */}
                                        <button
                                            onClick={endTutorial}
                                            className="px-3 py-2 text-white/50 hover:text-white hover:bg-white/10 rounded-lg text-sm transition-colors"
                                        >
                                            Skip
                                        </button>

                                        {/* Divider */}
                                        <div className="w-px h-8 bg-white/20" />

                                        {/* Navigation */}
                                        {currentStep > 0 && (
                                            <button
                                                onClick={prevStep}
                                                className="p-2.5 text-white/70 hover:text-white hover:bg-white/10 rounded-lg transition-colors"
                                                title="Previous"
                                            >
                                                <ChevronLeft className="w-5 h-5" />
                                            </button>
                                        )}
                                        <button
                                            onClick={nextStep}
                                            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-cyan-500 to-purple-600 text-white font-medium rounded-lg hover:opacity-90 transition-opacity"
                                        >
                                            {currentStep === tutorialSteps.length - 1 ? 'Finish' : 'Next'}
                                            <ChevronRight className="w-5 h-5" />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            )}
        </>
    )
}
